import React, { useEffect, useMemo, useState } from 'react';
import { MessageOutlined } from '@ant-design/icons';
import { FmButton } from './FmButton';
import { FmSectionBand, type FmSectionBandTone } from './FmReportComposition';
import { FmAccordion } from './FmAccordion';
import { suppressedSiblingTitles } from './fmHostedHeading';
import { FmSelector } from './FmSelector';
import { useFmResponsive } from './FmResponsive';
import { FmSection } from './sections/FmSection';
import { sectionLeadHeading } from './sections/sectionContent';
import type { FmSectionTone } from './sections/section.types';
import type {
  FmReportChapterDescriptor,
  FmReportChapterAssistantDescriptor,
  FmReportChapterAssistantTools,
  FmReportChapterSectionDescriptor,
  FmReportChapterTone,
} from './FmReportChapter.types';
import './FmReportChapter.css';

function chapterBandTone(tone: FmReportChapterTone = 'light'): FmSectionBandTone {
  if (tone === 'mist') return 'plain';
  if (tone === 'forest') return 'dark';
  if (tone === 'ink') return 'deep';
  if (tone === 'paper') return 'paper';
  return 'light';
}

/** The contrast an embedded section takes from the chapter band it sits on. */
function chapterSectionTone(tone: FmReportChapterTone = 'light'): FmSectionTone {
  if (tone === 'forest') return 'dark';
  if (tone === 'ink') return 'deep';
  if (tone === 'paper') return 'paper';
  return 'light';
}

const ChapterAssistant: React.FC<{
  assistant?: FmReportChapterAssistantDescriptor;
  assistantTools?: FmReportChapterAssistantTools;
  position: 'before-sections' | 'after-sections';
  testId: string;
}> = ({ assistant, assistantTools, position, testId }) => {
  if (!assistant) return null;
  const assistantPosition = assistant.placement === 'before-sections'
    ? 'before-sections'
    : 'after-sections';
  if (assistantPosition !== position) return null;
  return <div data-role="assistant">
    <FmButton
      variant="text"
      leadingIcon={<MessageOutlined />}
      disabled={!assistantTools}
      onClick={() => assistantTools?.explain(assistant)}
      data-testid={`${testId}-assistant`}
    >
      {assistant.actionLabel}
    </FmButton>
  </div>;
};

/**
 * Lead headings an earlier rendered sibling already printed, keyed by section
 * id. Computed over the sections that ACTUALLY RENDER, in reading order: detail
 * sections live behind a one-at-a-time view switcher, so counting all of them
 * would let a section lose its heading to a twin the reader cannot see.
 */
function usePrintedHeadings(
  chapterTitle: string,
  orderedSections: readonly FmReportChapterSectionDescriptor[],
  activeDetailIds: ReadonlySet<string>,
): ReadonlyMap<string, string> {
  return useMemo(() => {
    const renderedInOrder = [
      ...orderedSections.filter(({ role }) => role !== 'detail' && role !== 'action'),
      ...orderedSections.filter(
        ({ role, section }) => role === 'detail' && activeDetailIds.has(section.id),
      ),
      ...orderedSections.filter(({ role }) => role === 'action'),
    ];
    const headings = renderedInOrder.map(({ section }) => sectionLeadHeading(section));
    const suppressed = suppressedSiblingTitles(chapterTitle, headings);
    const printed = new Map<string, string>();
    renderedInOrder.forEach(({ section }, index) => {
      const heading = headings[index];
      if (suppressed[index] && heading) printed.set(section.id, heading);
    });
    return printed;
  }, [activeDetailIds, chapterTitle, orderedSections]);
}

const ChapterSection: React.FC<{
  entry: FmReportChapterSectionDescriptor;
  hostTone: FmSectionTone;
  printedHeading: string | undefined;
}> = ({ entry, hostTone, printedHeading }) => (
  <div
    className={`fm-report-chapter__section fm-report-chapter__section--${entry.role}`}
    data-role={entry.role}
  >
    <FmSection
      section={entry.section}
      presentation="embedded"
      headingLevel={3}
      hostTone={hostTone}
      printedHeading={printedHeading}
    />
  </div>
);

function useOrderedSections(chapter: FmReportChapterDescriptor) {
  const tier = useFmResponsive();
  return useMemo(() => {
    if (tier !== 'mobile') return chapter.sections;
    const order = new Map(chapter.mobileOrder.map((id, index) => [id, index]));
    return [...chapter.sections].sort((a, b) => (
      (order.get(a.section.id) ?? Number.MAX_SAFE_INTEGER)
      - (order.get(b.section.id) ?? Number.MAX_SAFE_INTEGER)
    ));
  }, [chapter.sections, chapter.mobileOrder, tier]);
}

function useActiveDetail(
  chapter: FmReportChapterDescriptor,
  detailSections: readonly FmReportChapterSectionDescriptor[],
) {
  const [activeDetailViewId, setActiveDetailViewId] = useState(
    chapter.detailViews?.[0]?.id ?? '',
  );
  useEffect(() => {
    if (!chapter.detailViews?.some(({ id }) => id === activeDetailViewId)) {
      setActiveDetailViewId(chapter.detailViews?.[0]?.id ?? '');
    }
  }, [activeDetailViewId, chapter.detailViews]);
  const activeDetailIds = useMemo(
    () => new Set(
      chapter.detailViews?.find(({ id }) => id === activeDetailViewId)?.sectionIds
        ?? detailSections.map(({ section }) => section.id),
    ),
    [activeDetailViewId, chapter.detailViews, detailSections],
  );
  return { activeDetailViewId, setActiveDetailViewId, activeDetailIds };
}

export const FmReportChapter: React.FC<{
  chapter: FmReportChapterDescriptor;
  assistantTools?: FmReportChapterAssistantTools;
  /** The first chapter of a report without a title band carries the page h1. */
  ownsPageHeading?: boolean;
}> = ({ chapter, assistantTools, ownsPageHeading = false }) => {
  const orderedSections = useOrderedSections(chapter);
  const detailSections = useMemo(
    () => orderedSections.filter(({ role }) => role === 'detail'),
    [orderedSections],
  );
  const { activeDetailViewId, setActiveDetailViewId, activeDetailIds } = useActiveDetail(
    chapter,
    detailSections,
  );
  // One section = one title: the chapter band prints the chapter heading, and
  // a section repeating it, or repeating a heading an earlier sibling printed,
  // renders titleless. Only the chapter holds the ordered list, so it settles
  // sibling repeats here.
  const printedHeadings = usePrintedHeadings(chapter.title, orderedSections, activeDetailIds);
  const hostTone = chapterSectionTone(chapter.tone);
  const testId = chapter.testId ?? `report-chapter-${chapter.id}`;
  const renderSection = (entry: FmReportChapterSectionDescriptor) => (
    <ChapterSection
      key={entry.section.id}
      entry={entry}
      hostTone={hostTone}
      printedHeading={printedHeadings.get(entry.section.id)}
    />
  );

  return <FmSectionBand
    id={chapter.id}
    eyebrow={chapter.eyebrow}
    title={chapter.title}
    titleLevel={ownsPageHeading ? 1 : 2}
    description={[chapter.literalAnswer, chapter.summary].filter(Boolean).join(' ')}
    tone={chapterBandTone(chapter.tone)}
    density={chapter.density ?? 'comfortable'}
    titleScale={chapter.scale ?? 'standard'}
    headerPlacement={chapter.headline ?? 'above'}
    width="full"
    bodyClassName="fm-report-chapter__sections"
    data-testid={testId}
    data-owner-question={chapter.ownerQuestion}
    data-dominant-beat={chapter.dominantBeat}
    data-transition={chapter.transition}
    data-conversion-role={chapter.conversionRole}
  >
    <ChapterAssistant
      assistant={chapter.recipe.assistant}
      assistantTools={assistantTools}
      position="before-sections"
      testId={testId}
    />
    {orderedSections.filter(({ role }) => role !== 'detail' && role !== 'action').map(renderSection)}
    <ChapterAssistant
      assistant={chapter.recipe.assistant}
      assistantTools={assistantTools}
      position="after-sections"
      testId={testId}
    />
    {chapter.detailLabel ? (
      <div className="fm-report-chapter__detail" data-role="detail">
        <FmAccordion
          plain
          defaultOpen={chapter.detailOpen ? [`${chapter.id}-expert-detail`] : undefined}
          items={[{
            key: `${chapter.id}-expert-detail`,
            label: chapter.detailLabel,
            children: (
              <div className="fm-report-chapter__detail-sections">
                {chapter.detailViews ? (
                  <FmSelector
                    variant="segmented"
                    label={`${chapter.title} expert evidence`}
                    value={activeDetailViewId}
                    onChange={setActiveDetailViewId}
                    items={chapter.detailViews.map(({ id, label }) => ({ value: id, label }))}
                    data-testid={`${testId}-detail-views`}
                  />
                ) : null}
                {detailSections
                  .filter(({ section }) => activeDetailIds.has(section.id))
                  .map(renderSection)}
              </div>
            ),
          }]}
          data-testid={`${testId}-expert-detail`}
        />
      </div>
    ) : null}
    {orderedSections.filter(({ role }) => role === 'action').map(renderSection)}
  </FmSectionBand>;
};

FmReportChapter.displayName = 'FmReportChapter';
